import { useMemo } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import { RotateCcw } from 'lucide-react'
import { renderToString } from 'react-dom/server'

import { numberFormatter } from '@fortem-monorepo/js-utilities'
import { CustomMenuItem } from '@fortem-monorepo/react-ag'
import { createColDefs, createContextMenu } from '@fortem-monorepo/react-ag'
import { CustomAgGrid } from '@fortem-monorepo/react-ag'
import { useUserContext } from '@fortem-monorepo/react-spa'
import { Hedging } from '@fortem-monorepo/web-sdk'

import {
  useResetHedgingEntryMutation,
  useUpdateHedgingEntryMutation,
} from '@/react-query/mutations/hedging'

type HedgingItem = Hedging.GetHedgingData[number]

export interface NtHedgingFxFwdTableProps extends React.ComponentProps<typeof CustomAgGrid> {}

const createHedgingColDefs = (hasAccess: boolean) =>
  createColDefs<HedgingItem>([
    {
      field: 'id',
      hide: true,
    },
    {
      field: 'value_date',
      hide: true,
    },
    {
      field: 'active',
      headerName: 'To Add',
      editable: hasAccess,
      width: 100,
      filter: false,
    },
    {
      field: 'fund_name',
      headerName: 'Fund',
      width: 100,
      rowGroup: true,
      hide: true,
    },
    {
      field: 'message',
      headerName: 'Message',
      width: 200,
      filter: false,
    },
    {
      headerName: 'IDs',
      groupId: 'IDs',
      children: [
        {
          field: 'deal_reference',
          headerName: 'Deal Ref',
          width: 125,
          cellStyle: { textAlign: 'center' },
        },
        {
          field: 'memo',
          headerName: 'Memo',
          width: 115,
          cellStyle: { textAlign: 'center' },
          editable: hasAccess,
        },
        {
          field: 'external_reference',
          headerName: 'Ext Ref',
          width: 175,
          cellStyle: { textAlign: 'center' },
          editable: hasAccess,
        },
        {
          field: 'scope',
          headerName: 'Scope',
          width: 175,
          cellStyle: { textAlign: 'center' },
          editable: hasAccess,
        },
        {
          field: 'unique_identifier',
          headerName: 'Product',
          width: 300,
          cellStyle: { textAlign: 'center' },
        },
      ],
    },
    {
      headerName: 'Trade Specs',
      groupId: 'Trade Specs',
      children: [
        {
          field: 'price',
          width: 125,
          valueFormatter: numberFormatter({
            delimiter: true,
            minimumFractionDigits: 6,
            maximumFractionDigits: 6,
          }),
          cellStyle: { textAlign: 'right' },
          editable: hasAccess,
        },
        {
          field: 'quantity',
          width: 135,
          valueFormatter: numberFormatter({
            delimiter: true,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }),
          cellStyle: { textAlign: 'right' },
          editable: hasAccess,
        },
      ],
    },
    {
      headerName: 'Dates',
      groupId: 'Dates',
      children: [
        {
          field: 'open_datetime_utc',
          headerName: 'Open DT',
          width: 160,
          filter: false,
          cellStyle: { textAlign: 'center' },
          editable: hasAccess,
        },
        {
          field: 'trade_datetime_utc',
          headerName: 'Trade DT',
          width: 160,
          filter: false,
          cellStyle: { textAlign: 'center' },
          editable: hasAccess,
        },
        {
          field: 'settlement_date',
          headerName: 'Settl DT',
          width: 125,
          filter: false,
          cellStyle: { textAlign: 'center' },
          editable: hasAccess,
        },
      ],
    },
  ])

export const NtHedgingFxFwdTable: React.FC<NtHedgingFxFwdTableProps> = ({ ...props }) => {
  const { hasPermissions } = useUserContext()

  const queryClient = useQueryClient()
  const resetHedgingEntryMutation = useResetHedgingEntryMutation()
  const updateHedgingEntryMutation = useUpdateHedgingEntryMutation()

  const hasWriteAccess = hasPermissions((permissions) => ({
    permissions: [permissions.WRITE_NT_HEDGING_FXFWD],
  }))

  const colDefs = useMemo(() => createHedgingColDefs(hasWriteAccess), [hasWriteAccess])

  const getContextMenuItems = createContextMenu<HedgingItem>((params) => {
    const deal_reference = params.node?.data?.deal_reference
    return [
      ...((hasWriteAccess && !!deal_reference
        ? [
            {
              name: `Reset ${params.node?.data?.deal_reference}`,
              icon: renderToString(<RotateCcw size={16} />),
              action: () => resetHedgingEntryMutation.mutateAsync({ deal_reference }),
              cssClasses: ['bg-red-100/50!', 'hover:bg-red-100!'],
            },
            'separator',
          ]
        : []) as CustomMenuItem<HedgingItem>[]),
      'copy',
      'copyWithHeaders',
      'copyWithGroupHeaders',
      'export',
    ]
  })

  return (
    <CustomAgGrid
      onCellValueChanged={async (e: any) => {
        const {
          data: rowData,
          oldValue,
          newValue,
          colDef: { field: updatedField },
        } = e

        const body = {
          rowData: rowData!,
          newValue: newValue!,
          oldValue: oldValue!,
          updatedField: updatedField!,
        }

        updateHedgingEntryMutation.mutateAsync(body)

        queryClient.invalidateQueries({ queryKey: ['hedging', 'fxfwd'] })
      }}
      columnDefs={colDefs}
      getContextMenuItems={getContextMenuItems}
      {...props}
    />
  )
}
